import { useEffect, useState } from "react";
import { SEO } from "@/components/SEO";
import { useParams, useNavigate } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { UserAvatar } from "@/components/UserAvatar";
import { parseGenres } from "@/data/gameGenres";
import { SteamProfileBlock } from "@/components/SteamProfileBlock";
import {
  User,
  Gamepad2,
  Calendar,
  ExternalLink,
  ArrowLeft,
  Globe,
  Building2,
  Mail,
  MessageSquare,
  Star,
  Twitch,
  Youtube,
  Music,
  Instagram,
  Twitter
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { getDeveloperDisplayName } from "@/lib/developerUtils";
import { StudioName } from "@/components/StudioName";
import { getCountryFlag } from "@/utils/countryMapping";

interface Developer {
  id: string;
  display_name: string;
  avatar_url: string | null;
  bio: string | null;
  website_url: string | null;
  studio_name: string | null;
  studio_logo_url: string | null;
  studio_slug: string | null;
  is_premium?: boolean;
  premium_end_date?: string | null;
  twitch_url: string | null;
  youtube_url: string | null;
  tiktok_url: string | null;
  instagram_url: string | null;
  twitter_url: string | null;
  bluesky_url: string | null;
  steam_url: string | null;
  steam_id: string | null;
  country: string | null;
  active_border_id: string | null;
  shop_items: { metadata: { style: string } } | null;
}

interface Game {
  id: string;
  title: string;
  description: string;
  genres: string[];
  cover_image_url: string;
  screenshots: string[];
  steam_url: string;
  itch_url: string;
  release_date: string;
  highlight_id: number;
  is_published: boolean;
  allow_requests: boolean;
  available_keys: number;
  total_keys: number;
}

export default function DeveloperProfile() {
  const { identifier } = useParams<{ identifier: string }>();
  const navigate = useNavigate();
  const [developer, setDeveloper] = useState<Developer | null>(null);
  const [games, setGames] = useState<Game[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (identifier) {
      fetchDeveloper();
    }
  }, [identifier]);

  // Fetch games when developer is loaded
  useEffect(() => {
    if (developer?.id) {
      fetchGames();
    }
  }, [developer]);

  const fetchDeveloper = async () => {
    if (!identifier) return;

    try {
      // Try to find by slug first, then by ID
      let query = supabase
        .from("profiles")
        .select("*, active_border_id, shop_items(metadata)")
        .eq("user_type", "developer");

      // Check if identifier looks like a UUID (ID) or a slug
      const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(identifier);

      if (isUUID) {
        query = query.eq("id", identifier);
      } else {
        query = query.eq("studio_slug", identifier);
      }

      const { data, error } = await query.single();

      if (error) {
        console.error("Error fetching developer:", error);
        toast.error("Developer not found");
        navigate("/");
        return;
      }

      setDeveloper({
        ...data,
        steam_id: (data as any).steam_id || null,
        country: (data as any).country || null
      });
    } catch (error) {
      console.error("Error:", error);
      toast.error("An error occurred");
      setLoading(false);
    }
  };

  const fetchGames = async () => {
    if (!developer?.id) return;

    try {
      const { data, error } = await (supabase as any)
        .from("games")
        .select("*")
        .eq("developer_id", developer.id)
        .eq("is_published", true)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching games:", error);
        setLoading(false);
        return;
      }

      const gamesWithParsedGenres = (data || []).map((game: any) => ({
        ...game,
        genres: parseGenres(game.genre || ""),
      }));

      setGames(gamesWithParsedGenres);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleGameClick = (gameId: string) => {
    navigate(`/game/${gameId}`);
  };

  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <div className="min-h-[calc(100vh-120px)] flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!developer) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <div className="min-h-[calc(100vh-120px)] flex items-center justify-center">
            <p className="text-muted-foreground">Developer not found</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <SEO
        title={getDeveloperDisplayName(developer)}
        description={developer.bio || `Check out games by ${getDeveloperDisplayName(developer)} on IndieKeys.`}
        image={developer.avatar_url || undefined}
      />
      <div className="container mx-auto px-4 py-8">
        {/* Back button */}
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <div className="flex flex-col-reverse md:flex-row gap-8">
          {/* Left side - Games list (70%) */}
          <div className="flex-1">
            <div className="mb-6">
              <h1 className="text-3xl font-bold mb-2">
                <StudioName
                  studioName={developer.studio_name}
                  displayName={developer.display_name}
                  isPremium={developer.is_premium}
                  premiumEndDate={developer.premium_end_date}
                />'s Games
              </h1>
              <p className="text-muted-foreground">
                {games.length} {games.length === 1 ? 'game' : 'games'} published
              </p>
            </div>

            {games.length === 0 ? (
              <Card className="p-8 text-center">
                <Gamepad2 className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-xl font-semibold mb-2">No games published yet</h3>
                <p className="text-muted-foreground">
                  This developer hasn't published any games yet.
                </p>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {games.map((game) => (
                  <Card
                    key={game.id}
                    className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer rounded-custom-paddings"
                    onClick={() => handleGameClick(game.id)}
                  >
                    <div className="aspect-video relative">
                      <img
                        src={game.cover_image_url}
                        alt={game.title}
                        className="w-full h-full object-cover rounded-custom-paddings p-0"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                      <div className="absolute inset-0 bg-black/20 rounded-custom-paddings opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center">
                        <Button size="sm" variant="secondary">
                          View Details
                        </Button>
                      </div>
                    </div>
                    <CardContent className="p-4">
                      <h3 className="font-bold text-lg mb-2 line-clamp-1 mb-0">{game.title}</h3>
                      <span className="text-sm text-muted-foreground mb-2 block">{new Date(game.release_date).toLocaleDateString()}</span>
                      <div className="flex flex-wrap gap-1 mb-3">
                        {game.genres.slice(0, 4).map((genre) => (
                          <Badge key={genre} variant="secondary" className="text-xs">
                            {genre}
                          </Badge>
                        ))}
                        {game.genres.length > 3 && (
                          <Badge variant="outline" className="text-xs">
                            +{game.genres.length - 3}
                          </Badge>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* Right side - Developer profile (30%) - Sticky */}
          <div className="md:w-80 flex-shrink-0">
            <div className="sticky top-8">
              <Card className="overflow-hidden">
                <CardHeader className="text-center pb-4">
                  <div className="relative">
                    {developer.avatar_url ? (
                      <UserAvatar
                        src={developer.avatar_url || undefined}
                        fallback={developer.display_name?.charAt(0) || "D"}
                        size="xl"
                        borderStyle={(developer as any).shop_items?.metadata?.style}
                        className="mx-auto mb-4"
                      />
                    ) : (
                      <div className="w-24 h-24 rounded-full mx-auto mb-4 bg-muted flex items-center justify-center">
                        <span className="text-2xl font-bold">
                          {getDeveloperDisplayName(developer).charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                  </div>

                  <CardTitle className="text-2xl">
                    <StudioName
                      studioName={developer.studio_name}
                      displayName={developer.display_name}
                      isPremium={developer.is_premium}
                      premiumEndDate={developer.premium_end_date}
                    />
                  </CardTitle>

                  {/* Country Flag */}
                  {developer.country && getCountryFlag(developer.country) && (
                    <div className="flex items-center justify-center gap-2 mt-2 mb-2">
                      <img
                        src={getCountryFlag(developer.country) || ''}
                        alt={developer.country}
                        className="w-6 h-auto object-cover rounded-sm shadow-sm"
                        title={developer.country}
                      />
                      <span className="text-sm text-muted-foreground">{developer.country}</span>
                    </div>
                  )}

                  {/* Studio Info */}
                  {(developer.website_url || developer.studio_logo_url) && (
                    <div>
                      <div className="space-y-2 mt-2">
                        {developer.website_url && (
                          <a
                            href={developer.website_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex justify-center items-center gap-2 text-sm text-primary hover:underline"
                          >
                            <Globe className="w-4 h-4" />
                            Website
                          </a>
                        )}
                        {developer.studio_logo_url && (
                          <div className="flex items-center gap-2 justify-center">
                            <img
                              src={developer.studio_logo_url}
                              alt="Studio logo"
                              className="w-8 h-8 rounded object-cover"
                            />
                            <span className="text-sm text-muted-foreground">Studio Logo</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </CardHeader>

                <CardContent className="space-y-6 overflow-y-auto max-h-[calc(100vh-200px)]">
                  {/* Bio */}
                  {developer.bio && (
                    <div>
                      <h4 className="font-semibold mb-2 flex items-center gap-2">
                        <User className="w-4 h-4" />
                        About
                      </h4>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {developer.bio.length > 250
                          ? `${developer.bio.substring(0, 250)}...`
                          : developer.bio
                        }
                      </p>
                    </div>
                  )}



                  {/* Social Media */}
                  {(developer.twitch_url || developer.youtube_url || developer.tiktok_url || developer.instagram_url || developer.twitter_url || developer.bluesky_url || developer.steam_url) && (
                    <div>
                      <h4 className="font-semibold mb-3 flex items-center gap-2">
                        <Twitter className="w-4 h-4" />
                        Social Media
                      </h4>
                      <div className="grid grid-cols-2 gap-2">
                        {/* Twitch */}
                        {developer.twitch_url && (
                          <a
                            href={developer.twitch_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center gap-2 px-3 py-2 text-sm bg-purple-500/10 border border-purple-500/20 text-purple-600 hover:bg-purple-500/20 hover:text-purple-700 rounded-custom-paddings transition-colors"
                          >
                            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z" />
                            </svg>
                            Twitch
                          </a>
                        )}

                        {/* YouTube */}
                        {developer.youtube_url && (
                          <a
                            href={developer.youtube_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center gap-2 px-3 py-2 text-sm bg-red-500/10 border border-red-500/20 text-red-600 hover:bg-red-500/20 hover:text-red-700 rounded-custom-paddings transition-colors"
                          >
                            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                            </svg>
                            YouTube
                          </a>
                        )}

                        {/* TikTok */}
                        {developer.tiktok_url && (
                          <a
                            href={developer.tiktok_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center gap-2 px-3 py-2 text-sm bg-pink-500/10 border border-pink-500/20 text-pink-600 hover:bg-pink-500/20 hover:text-pink-700 rounded-custom-paddings transition-colors"
                          >
                            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                            </svg>
                            TikTok
                          </a>
                        )}

                        {/* Instagram */}
                        {developer.instagram_url && (
                          <a
                            href={developer.instagram_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center gap-2 px-3 py-2 text-sm bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-custom-paddings hover:opacity-90 transition-opacity"
                          >
                            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                            </svg>
                            Instagram
                          </a>
                        )}

                        {/* Twitter */}
                        {developer.twitter_url && (
                          <a
                            href={developer.twitter_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center gap-2 px-3 py-2 text-sm bg-black text-white rounded-custom-paddings hover:bg-gray-800 transition-colors"
                          >
                            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                            </svg>
                            X
                          </a>
                        )}

                        {/* Bluesky */}
                        {developer.bluesky_url && (
                          <a
                            href={developer.bluesky_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center gap-2 px-3 py-2 text-sm bg-sky-500 text-white rounded-custom-paddings hover:bg-sky-600 transition-colors"
                          >
                            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M12 10.8c-1.087-2.183-4.046-6.053-6.798-7.995C2.566.944 1.561 1.266.902 1.565.139 1.908 0 3.08 0 3.768c0 .69.378 5.65.624 6.479.815 2.736 3.713 3.66 6.383 3.364.136-.02.275-.039.415-.056-.138.22-.254.455-.345.705-.18.5-.275 1.03-.275 1.58 0 1.418.488 2.73 1.375 3.78.89 1.05 2.205 1.65 3.625 1.65s2.735-.6 3.625-1.65c.887-1.05 1.375-2.362 1.375-3.78 0-.55-.095-1.08-.275-1.58-.091-.25-.207-.485-.345-.705.14.017.279.036.415.056 2.67.296 5.568-.628 6.383-3.364.246-.829.624-5.79.624-6.478 0-.69-.139-2.861-.902-3.204-.659-.299-1.664-.621-4.3 1.24C16.046 4.747 13.087 8.617 12 10.8z" />
                            </svg>
                            Bluesky
                          </a>
                        )}

                        {/* Steam */}
                        {developer.steam_url && (
                          <a
                            href={developer.steam_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium bg-gradient-to-r from-[#2a475e] to-[#3c5a6b] text-white rounded-custom-paddings hover:from-[#3c5a6b] hover:to-[#4a6b7a] transition-all duration-200 shadow-lg hover:shadow-xl border border-[#4a6b7a] ring-2 ring-[#2a475e]/20"
                          >
                            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M11.979 0C5.678 0 .511 4.86.022 11.037l6.432 2.658c.545-.371 1.203-.59 1.912-.59.063 0 .125.004.188.006l2.861-4.142V8.91c0-2.495 2.028-4.524 4.524-4.524 2.494 0 4.524 2.031 4.524 4.527s-2.03 4.525-4.524 4.525h-.105l-4.076 2.911c0 .052.004.105.004.159 0 1.875-1.515 3.396-3.39 3.396-1.635 0-3.016-1.173-3.331-2.727L.436 15.27C1.862 20.307 6.486 24 11.979 24c6.627 0 11.999-5.373 11.999-12S18.605 0 11.979 0zM7.54 18.21l-1.473-.61c.262.543.714.999 1.314 1.25 1.297.539 2.793-.076 3.332-1.375.263-.63.264-1.319.005-1.949s-.75-1.121-1.377-1.383c-.624-.26-1.29-.249-1.878-.03l1.523.63c.956.4 1.409 1.5 1.009 2.455-.397.957-1.497 1.41-2.454 1.012H7.54zm11.415-9.303c0-1.662-1.353-3.015-3.015-3.015-1.665 0-3.015 1.353-3.015 3.015 0 1.665 1.35 3.015 3.015 3.015 1.663 0 3.015-1.35 3.015-3.015zm-5.273-.005c0-1.252 1.013-2.266 2.265-2.266 1.249 0 2.266 1.014 2.266 2.266 0 1.251-1.017 2.265-2.266 2.265-1.253 0-2.265-1.014-2.265-2.265z" />
                            </svg>
                            Steam
                          </a>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Steam Profile Block */}
                  {developer.steam_id && (
                    <div>
                      <h4 className="font-semibold mb-3 flex items-center gap-2">
                        <Gamepad2 className="w-4 h-4" />
                        Steam Profile
                      </h4>
                      <SteamProfileBlock
                        steamId={developer.steam_id}
                        steamUrl={developer.steam_url}
                      />
                    </div>
                  )}

                  {/* Stats */}
                  <div>
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      <Star className="w-4 h-4" />
                      Statistics
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Games Published:</span>
                        <span className="font-medium">{games.length}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Given Keys:</span>
                        <span className="font-medium">
                          {games.reduce((sum, game) => sum + (game.total_keys - game.available_keys), 0)}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
